import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DataGrid from '../components/DataGrid';
import LoadingIndicator from '../components/LoadingIndicator';
import { configureExternalRoles } from '../services/roleService';
import { toast } from 'react-toastify';
import '../styles/ConfigExternalRolesPage.css';

const ConfigExternalRolesPage = () => {
  const [externalRoles, setExternalRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Mock data for initial development
  useEffect(() => {
    const mockData = [
      { id: 1, externalRoleName: 'Admin', internalRoleName: 'Administrator', active: true },
      { id: 2, externalRoleName: 'User', internalRoleName: 'Standard User', active: true },
      { id: 3, externalRoleName: 'ReportViewer', internalRoleName: 'Report Viewer', active: true },
      { id: 4, externalRoleName: 'ReportEditor', internalRoleName: 'Report Editor', active: false },
    ];
    
    setExternalRoles(mockData);
  }, []);
  
  const handleToggleActive = (roleId) => {
    setExternalRoles(
      externalRoles.map(role => 
        role.id === roleId ? { ...role, active: !role.active } : role
      )
    );
  };
  
  const handleSaveConfiguration = async () => {
    try {
      setSaving(true);
      // In a real implementation, this would send the data to the API
      await configureExternalRoles(externalRoles);
      toast.success('External roles configuration saved successfully');
    } catch (err) {
      setError('Failed to save external roles configuration');
      toast.error('Failed to save external roles configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const columns = [
    { field: 'externalRoleName', headerName: 'External Role Name' },
    { field: 'internalRoleName', headerName: 'Internal Role Name' },
    { 
      field: 'active', 
      headerName: 'Status',
      renderCell: (row) => (
        <span className={`status-badge ${row.active ? 'active' : 'inactive'}`}>
          {row.active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: (row) => (
        <div className="action-buttons">
          <button 
            className={`toggle-button ${row.active ? 'deactivate' : 'activate'}`}
            onClick={() => handleToggleActive(row.id)}
          >
            {row.active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      )
    }
  ];
  
  return (
    <div className="config-external-roles-page">
      <LoadingIndicator isLoading={loading || saving} message={saving ? 'Saving configuration...' : 'Loading...'} />
      
      <h2>Configure External Roles</h2>
      <p className="info-text">
        Map external authentication roles to internal application roles.
      </p>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="grid-container">
        <DataGrid 
          columns={columns}
          data={externalRoles}
          pageSize={10}
          loading={loading}
          noDataMessage="No external roles configured"
        />
      </div>
      
      <div className="action-container">
        <button 
          className="save-button"
          onClick={handleSaveConfiguration}
          disabled={saving}
        >
          Save Configuration
        </button>
        <Link to="/manage-roles" className="secondary-button">
          Manage Roles
        </Link>
      </div>
    </div>
  );
};

export default ConfigExternalRolesPage;